import PropTypes from 'prop-types';
import { IconButton, TableCell, TableRow, Typography } from '@tempmee/react';
import { OutlineChevronDownIcon, OutlineChevronUpIcon } from '@tempmee/react-icons';
import { UserProfession } from '../../../../../enums/UserProfession';
import { AuthorizationCredentialDetail } from './AuthorizationCredentialDetail';
import {
  authorizationShape,
  credentialRuleShape,
  getStatusConfig,
  monitoredCredentialShape,
  professionShape,
} from './credentialV2Utils';

/** Columns in the credentials table, including the expand toggle. */
export const AUTHORIZATION_TABLE_COLUMNS = 4;

/**
 * One authorization in the credentials table: a summary row and, when expanded, a
 * full-width row holding its credential cards.
 */
export const AuthorizationCredentialItem = ({
  authorization,
  isExpanded,
  handleToggle,
  allProfessions,
  credentialRules,
  onDelete,
  onSave,
  monitoredCredentials,
}) => {
  const professionName =
    UserProfession.getName(authorization?.profession) || authorization?.profession;

  const statusConfig = getStatusConfig(authorization?.status);
  const detailId = `authorization-detail-${authorization?.id}`;
  const ToggleIcon = isExpanded ? OutlineChevronUpIcon : OutlineChevronDownIcon;
  const toggle = () => handleToggle(authorization?.id);

  return (
    <>
      {/* The whole row toggles on click, as before the DS migration; the IconButton
          stays the keyboard and screen-reader control. */}
      <TableRow hover selected={isExpanded} className="cursor-pointer" onClick={toggle}>
        <TableCell>{professionName}</TableCell>
        <TableCell>{authorization?.state}</TableCell>
        <TableCell>
          <Typography variant="body2" bold className={statusConfig.className}>
            {statusConfig.label}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <IconButton
            Icon={<ToggleIcon size={20} />}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${professionName} ${
              authorization?.state ?? ''
            }`.trim()}
            aria-expanded={isExpanded}
            aria-controls={isExpanded ? detailId : undefined}
            onClick={(e) => {
              // Don't let the row's own click handler toggle it straight back.
              e.stopPropagation();
              toggle();
            }}
          />
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow id={detailId}>
          {/* Default padding absorbs the detail's Bootstrap `.row` negative margins, which
              would otherwise overhang the cell and scroll the table horizontally. */}
          <TableCell colSpan={AUTHORIZATION_TABLE_COLUMNS}>
            <AuthorizationCredentialDetail
              authorization={authorization}
              allProfessions={allProfessions}
              credentialRules={credentialRules}
              onDelete={onDelete}
              onSave={onSave}
              monitoredCredentials={monitoredCredentials}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

AuthorizationCredentialItem.propTypes = {
  authorization: PropTypes.shape(authorizationShape).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  /** Receives the authorization id. */
  handleToggle: PropTypes.func.isRequired,
  allProfessions: PropTypes.arrayOf(PropTypes.shape(professionShape)),
  credentialRules: PropTypes.arrayOf(PropTypes.shape(credentialRuleShape)),
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  monitoredCredentials: PropTypes.arrayOf(PropTypes.shape(monitoredCredentialShape)),
};

AuthorizationCredentialItem.defaultProps = {
  allProfessions: [],
  credentialRules: [],
  monitoredCredentials: [],
};
